import { form } from './variables';

/**
 * Cloudflare Turnstile token for spam-gated projects.
 *
 * The API tells us per project whether a token is required
 * (`form.captcha.required` + `form.captcha.site_key`, see WidgetChannelResource).
 * For every other project this module is a no-op and nothing is loaded.
 *
 * NOTE on the "no CDN dependencies" rule: Turnstile cannot be self-hosted — the
 * challenge script must come from challenges.cloudflare.com. It is loaded lazily
 * and ONLY for projects with captcha.required, so the fleet default is unchanged.
 *
 * Tokens are single-use: every call renders/executes a fresh challenge.
 */
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TIMEOUT_MS = 12000;

let scriptPromise = null;

/**
 * purpose 'lead'  → token wanted when the project requires captcha on submissions.
 * purpose 'otp'   → also when the API asks for a token on every OTP send
 *                   (captcha.otp_required, fleet-wide SMS-pumping guard).
 */
function captchaConfig(purpose = 'lead') {
    const c = form?.captcha;
    if (!c || !c.site_key) return null;
    const wanted = purpose === 'otp' ? (c.required || c.otp_required) : c.required;
    return wanted ? c : null;
}

export function isCaptchaRequired() {
    return !!captchaConfig('lead');
}

function loadScript() {
    if (window.turnstile) return Promise.resolve();
    if (scriptPromise) return scriptPromise;
    scriptPromise = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = SCRIPT_SRC;
        s.async = true;
        s.defer = true;
        s.onload = () => resolve();
        s.onerror = () => { scriptPromise = null; reject(new Error('turnstile script failed')); };
        document.head.appendChild(s);
    });
    return scriptPromise;
}

function container() {
    let el = document.getElementById('pp-turnstile');
    if (!el) {
        el = document.createElement('div');
        el.id = 'pp-turnstile';
        // Managed mode is usually invisible; when Cloudflare escalates to an
        // interactive check the iframe renders here, centered at the bottom.
        el.style.cssText = 'position:fixed;left:50%;bottom:12px;transform:translateX(-50%);z-index:2147483647;';
        document.body.appendChild(el);
    }
    return el;
}

/**
 * Resolve to a token string, or null when the project does not require captcha.
 * Rejects if the challenge could not be completed (script blocked, timeout, error)
 * so the caller surfaces "please try again" instead of a silent 422.
 */
export async function getTurnstileToken(purpose = 'lead') {
    const cfg = captchaConfig(purpose);
    if (!cfg) return null;

    await loadScript();

    return new Promise((resolve, reject) => {
        const el = container();
        el.innerHTML = '';
        let done = false;
        const timer = setTimeout(() => {
            if (done) return;
            done = true;
            try { window.turnstile.remove(widgetId); } catch (e) { /* noop */ }
            reject(new Error('turnstile timeout'));
        }, TIMEOUT_MS);
        const finish = (fn) => (v) => {
            if (done) return;
            done = true;
            clearTimeout(timer);
            try { window.turnstile.remove(widgetId); } catch (e) { /* noop */ }
            el.innerHTML = '';
            fn(v);
        };
        let widgetId;
        try {
            widgetId = window.turnstile.render(el, {
                sitekey: cfg.site_key,
                action: purpose,
                callback: finish(resolve),
                'error-callback': finish(() => reject(new Error('turnstile error'))),
                'expired-callback': finish(() => reject(new Error('turnstile expired'))),
            });
        } catch (e) {
            finish(reject)(e);
        }
    });
}

/** Merge a token into a request payload when the project requires one. */
export async function withTurnstile(data, purpose = 'lead') {
    const token = await getTurnstileToken(purpose);
    return token ? { ...(data || {}), cf_turnstile_token: token } : data;
}
